/**
 * Pure geometry helpers for the Divergence Timeline.
 *
 * Everything here works in CSS pixels and is derived from the container's
 * measured size — there are no hard-coded coordinates. Keeping these functions
 * free of DOM access lets the layout, the hit areas, the ring and the
 * connectors all be computed from one shared source of truth, and unit-tested
 * without a browser.
 *
 * ── Tuning ───────────────────────────────────────────────────────────────────
 * Circle size            → RADIUS_RATIO below (component may override per call)
 * Wave motion            → DEFAULT_WAVES (amplitude/frequency/speed per wave)
 * Edge grain density     → EdgeGrainOptions passed to buildEdgeGrain
 * Connector geometry     → `elbowRun` passed to elbowConnector
 */

export interface Point {
  x: number;
  y: number;
}

export interface CircleLayout {
  /** Centre of the ring, relative to the container's top-left. */
  cx: number;
  cy: number;
  /** Nominal radius of the ring. The wave and grain build the organic edge
   *  outward/inward from it; this value itself is a perfect circle. */
  radius: number;
}

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface TopicLike {
  id: string;
  angle: number;
}

export interface LayoutOptions {
  /** Radius as a fraction of the container's shorter side. */
  radiusRatio?: number;
}

/** Radius as a fraction of the shorter side. Larger = more dominant circle. */
export const RADIUS_RATIO = 0.46;
/** Below this container width the component switches to its compact layout. */
export const MOBILE_BREAKPOINT = 768;

const isFiniteNumber = (n: number): boolean => typeof n === 'number' && Number.isFinite(n);
const TAU = Math.PI * 2;

/**
 * Ring layout for a container of the given size: always centred, sized by the
 * container's *shorter* side so it never clips on a short viewport.
 *
 * Returns null for a container that has not been measured yet.
 */
export function getCircleLayout(
  width: number,
  height: number,
  options: LayoutOptions = {}
): CircleLayout | null {
  if (!isFiniteNumber(width) || !isFiniteNumber(height)) return null;
  if (width <= 0 || height <= 0) return null;

  const radiusRatio = isFiniteNumber(options.radiusRatio ?? NaN)
    ? (options.radiusRatio as number)
    : RADIUS_RATIO;

  const radius = Math.min(width, height) * radiusRatio;
  if (radius <= 0) return null;
  return { cx: width / 2, cy: height / 2, radius };
}

/** A point on (or offset from) the ring at the given angle. */
export function pointOnCircle(layout: CircleLayout, angle: number, radiusOffset = 0): Point {
  const r = layout.radius + radiusOffset;
  return { x: layout.cx + Math.cos(angle) * r, y: layout.cy + Math.sin(angle) * r };
}

/**
 * Radius of each topic's interactive area, derived from how much arc each
 * topic owns. Capped below half the spacing so neighbouring areas can never
 * overlap — a topic must never be activated by its neighbour's hitbox.
 */
export function topicHitRadius(layout: CircleLayout, topicCount: number): number {
  if (topicCount <= 0) return 0;
  const spacing = (TAU * layout.radius) / topicCount;
  return Math.max(16, Math.min(spacing * 0.45, layout.radius * 0.36));
}

/** Distance from a rectangle's centre to its edge along a unit direction. */
export function rectRadiusAlong(halfW: number, halfH: number, ux: number, uy: number): number {
  if (!isFiniteNumber(halfW) || !isFiniteNumber(halfH)) return 0;
  const tx = ux === 0 ? Infinity : Math.abs(halfW / ux);
  const ty = uy === 0 ? Infinity : Math.abs(halfH / uy);
  const t = Math.min(tx, ty);
  return Number.isFinite(t) ? t : 0;
}

/**
 * The visible part of a connector between two circular endpoints: the segment
 * that starts on the source's edge and stops on the destination's edge, so it
 * never runs into or through either one.
 */
export function connectorLine(
  source: Point & { radius: number },
  target: Point & { radius: number }
): Segment | null {
  if (
    !isFiniteNumber(source.x) ||
    !isFiniteNumber(source.y) ||
    !isFiniteNumber(target.x) ||
    !isFiniteNumber(target.y) ||
    !isFiniteNumber(source.radius) ||
    !isFiniteNumber(target.radius)
  ) {
    return null;
  }

  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance === 0) return null;

  const ux = dx / distance;
  const uy = dy / distance;
  if (source.radius + target.radius >= distance) return null;

  return {
    x1: source.x + ux * source.radius,
    y1: source.y + uy * source.radius,
    x2: target.x - ux * target.radius,
    y2: target.y - uy * target.radius,
  };
}

/** `connectorLine` output as an SVG path. */
export function segmentToPath(segment: Segment | null): string {
  if (!segment) return '';
  const { x1, y1, x2, y2 } = segment;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} L ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

/**
 * The topic whose interactive area contains `point`, or null. Resolves by
 * stable topic id and picks the nearest match, so a point can never resolve to
 * a neighbouring topic.
 */
export function topicAtPoint<T extends TopicLike>(
  topics: readonly T[],
  layout: CircleLayout,
  point: Point,
  hitRadius: number
): T | null {
  if (!isFiniteNumber(point.x) || !isFiniteNumber(point.y)) return null;
  let best: T | null = null;
  let bestDistance = Infinity;
  for (const topic of topics) {
    const centre = pointOnCircle(layout, topic.angle);
    const distance = Math.hypot(point.x - centre.x, point.y - centre.y);
    if (distance <= hitRadius && distance < bestDistance) {
      best = topic;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * A technical connector: one straight run out of the text, then a diagonal to
 * a point on the perimeter. Returned as points so callers can animate the
 * draw-in with stroke-dash.
 *
 * The run is horizontal by default. A topic sitting level with the text has no
 * vertical travel of its own, so a horizontal run would put all three points on
 * one line; those get a vertical run instead. Either way there is exactly one
 * corner — no connector is ever a plain straight line.
 *
 * `elbowRun` is the length of that first segment; its sign follows the
 * direction of travel, so the elbow always sits between the text and the ring.
 */
export function elbowConnector(textAnchor: Point, perimeter: Point, elbowRun = 46): Point[] | null {
  if (
    !isFiniteNumber(textAnchor.x) ||
    !isFiniteNumber(textAnchor.y) ||
    !isFiniteNumber(perimeter.x) ||
    !isFiniteNumber(perimeter.y)
  ) {
    return null;
  }
  const dx = perimeter.x - textAnchor.x;
  const dy = perimeter.y - textAnchor.y;
  if (dx === 0 && dy === 0) return null;

  // A topic level with the text has no vertical travel, so a horizontal run
  // would leave all three points collinear. Turn the run vertical instead: the
  // corner then reads the same as every other connector, just mirrored.
  if (Math.abs(dy) <= 1) {
    const vDirection = Math.sign(dy) || 1;
    const vRun = Math.abs(elbowRun) * vDirection;
    return [textAnchor, { x: textAnchor.x, y: textAnchor.y + vRun }, perimeter];
  }

  // A topic directly above or below the text has no horizontal travel of its
  // own, so give it the full run rather than collapsing to a straight line —
  // every connector is meant to have a corner.
  const direction = Math.sign(dx) || 1;
  const available = Math.abs(dx) > 1 ? Math.abs(dx) * 0.8 : Math.abs(elbowRun);
  const run = Math.min(Math.abs(elbowRun), available) * direction;

  const elbow = { x: textAnchor.x + run, y: textAnchor.y };
  return [textAnchor, elbow, perimeter];
}

/** Points as an SVG polyline path. */
export function pointsToPath(points: Point[] | null): string {
  if (!points || points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
}

/** Total length of a polyline, for stroke-dash draw-in animations. */
export function polylineLength(points: Point[] | null): number {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

/** Small, fast, deterministic PRNG so a seed always yields the same layout. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Traveling wave ring ─────────────────────────────────────────────────────
// The ring is a real thin stroked circle whose radius deforms slightly as a
// wave travels around it — never a uniform breathing scale. Several waves at
// different speeds/frequencies combine so the motion never obviously repeats.

export interface WaveConfig {
  /** Wave amplitude, as a fraction of the radius. */
  amplitude?: number;
  /** How many humps travel around the circumference. */
  frequency?: number;
  /** Full turns per second the wave travels; sign sets direction. */
  speed?: number;
}

export interface RingBump {
  /** Angle on the ring the bump is centred on. */
  angle: number;
  /** Bump height, as a fraction of the radius. */
  amplitude?: number;
  /** Angular half-width (radians) of the bump — smaller is a tighter lump. */
  width?: number;
}

export interface RingWaveOptions {
  waves?: WaveConfig[];
  /** Local, mostly-static distortions — one per service marker. */
  bumps?: RingBump[];
  /** Overall radius offset, as a fraction of the radius. Used to draw the
   *  secondary line slightly inside the primary one. */
  radiusOffset?: number;
  /** Seconds elapsed; drives the travelling motion. */
  time?: number;
  /** Outline sample count. Higher is smoother, slightly more CPU. */
  points?: number;
}

/** Gentle default motion: three waves, none sharing a frequency or speed. */
export const DEFAULT_WAVES: WaveConfig[] = [
  { amplitude: 0.007, frequency: 3, speed: 0.05 },
  { amplitude: 0.0045, frequency: 5, speed: -0.035 },
  { amplitude: 0.0028, frequency: 8, speed: 0.07 },
];

/** Radius at a given angle: the exact circle, plus travelling wave, plus any
 *  local bumps (service markers, or a hover's extra distortion). */
export function waveRadiusAt(
  layout: CircleLayout,
  angle: number,
  options: RingWaveOptions = {}
): number {
  const { waves = DEFAULT_WAVES, bumps = [], radiusOffset = 0, time = 0 } = options;
  let n = 0;
  for (const w of waves) {
    const amplitude = w.amplitude ?? 0.005;
    const frequency = w.frequency ?? 3;
    const speed = w.speed ?? 0.05;
    n += amplitude * Math.sin(angle * frequency + time * speed * TAU);
  }
  for (const b of bumps) {
    const amplitude = b.amplitude ?? 0.01;
    const width = b.width ?? 0.12;
    let d = angle - b.angle;
    d = Math.atan2(Math.sin(d), Math.cos(d)); // wrap to [-pi, pi]
    n += amplitude * Math.exp(-(d * d) / (2 * width * width));
  }
  return layout.radius * (1 + radiusOffset + n);
}

/**
 * A closed path around the ring using waveRadiusAt, smoothly interpolated
 * with quadratic curves so the motion reads as fluid rather than faceted.
 */
export function buildWaveRingPath(layout: CircleLayout, options: RingWaveOptions = {}): string {
  if (!isFiniteNumber(layout.radius) || layout.radius <= 0) return '';
  const count = Math.max(24, Math.round(options.points ?? 160));
  const pts: Point[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * TAU;
    const r = waveRadiusAt(layout, angle, options);
    pts.push({ x: layout.cx + Math.cos(angle) * r, y: layout.cy + Math.sin(angle) * r });
  }

  const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const start = mid(pts[count - 1], pts[0]);
  let d = `M ${start.x.toFixed(2)} ${start.y.toFixed(2)}`;
  for (let i = 0; i < count; i++) {
    const cur = pts[i];
    const next = pts[(i + 1) % count];
    const m = mid(cur, next);
    d += ` Q ${cur.x.toFixed(2)} ${cur.y.toFixed(2)} ${m.x.toFixed(2)} ${m.y.toFixed(2)}`;
  }
  return `${d} Z`;
}

// ─── Edge grain ──────────────────────────────────────────────────────────────
// A sparse scatter of dots hugging the ring — enough to read as "not a
// mechanically perfect vector line," never a dense cloud. Concentrated just
// inside the edge; only a few dots stray outward.

export interface GrainDot {
  x: number;
  y: number;
  r: number;
  opacity: number;
}

export interface EdgeGrainOptions {
  seed?: number;
  count?: number;
  /** How far inside the ring the band reaches, as a fraction of the radius. */
  innerBand?: number;
  /** How far outside the ring a few stray dots may sit. */
  outerBand?: number;
  /** Chance (0..1) a given dot lands outside rather than inside the ring. */
  outerChance?: number;
  /** Peak dot radius in px. */
  dotSize?: number;
}

export function buildEdgeGrain(layout: CircleLayout, options: EdgeGrainOptions = {}): GrainDot[] {
  const {
    seed = 1,
    count = 220,
    innerBand = 0.035,
    outerBand = 0.012,
    outerChance = 0.14,
    dotSize = 1.2,
  } = options;
  if (!isFiniteNumber(layout.radius) || layout.radius <= 0) return [];

  const rand = mulberry32(seed * 7919 + 13);
  const n = Math.max(0, Math.round(count));
  const out: GrainDot[] = [];
  for (let i = 0; i < n; i++) {
    const angle = rand() * TAU;
    const outward = rand() < outerChance;
    const u = rand();
    const band = outward ? outerBand : innerBand;
    const offset = (outward ? 1 : -1) * u * u * band * layout.radius;
    const r = layout.radius + offset;
    const fade = 1 - Math.abs(offset) / (layout.radius * band || 1);
    out.push({
      x: layout.cx + Math.cos(angle) * r,
      y: layout.cy + Math.sin(angle) * r,
      r: dotSize * (0.4 + rand() * 0.6),
      opacity: Math.max(0.04, 0.1 + fade * 0.3) * (0.6 + rand() * 0.4),
    });
  }
  return out;
}
